import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Product } from '../types';
import { getProducts } from '../lib/api';
import { useCart } from '../state/cart.store';
import { useToast } from './Toast';
import ImageGallery from './ImageGallery';

type RouteParams = {
    groupSlug: string;
};

function formatEuro(amount: number): string {
    return amount.toFixed(2);
}

const ProductPage: React.FC = () => {
    const { groupSlug } = useParams<RouteParams>();
    const { addItem, totalQuantity } = useCart();
    const toast = useToast();

    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
    const [quantity, setQuantity] = useState(1);
    const [justAdded, setJustAdded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getProducts();
                if (!cancelled) setProducts(data);
            } catch {
                if (!cancelled) setError('Impossibile caricare il prodotto');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    const variants = useMemo(() => {
        return products
            .filter(p => p.groupSlug === groupSlug)
            .slice()
            .sort((a, b) => {
                const al = a.variantLabel ?? '';
                const bl = b.variantLabel ?? '';
                return al.localeCompare(bl);
            });
    }, [groupSlug, products]);

    const base = variants[0];
    const categorySlug = base?.categorySlug;

    useEffect(() => {
        if (variants.length > 0 && selectedProductId == null) {
            setSelectedProductId(variants[0].id);
        }
    }, [selectedProductId, variants]);

    const selectedVariant = useMemo(() => {
        if (selectedProductId == null) return undefined;
        return variants.find(v => v.id === selectedProductId);
    }, [selectedProductId, variants]);

    const total = (selectedVariant?.price ?? 0) * quantity;

    const onAdd = () => {
        if (!selectedVariant) return;
        const qty = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
        addItem(selectedVariant.id, qty);

        const name = selectedVariant.variantLabel
            ? `${selectedVariant.name} (${selectedVariant.variantLabel})`
            : selectedVariant.name;
        toast.show(`${name} x${qty} aggiunto al carrello`);

        setJustAdded(true);
        setTimeout(() => setJustAdded(false), 1500);
        setQuantity(1);
    };

    return (
        <div className="product-page">
            <p>
                {categorySlug ? <Link to={`/category/${categorySlug}`}>← Torna alla categoria</Link> : <Link to="/">← Home</Link>}
            </p>

            <div className="menu-header">
                <h2>{base?.name ?? 'Prodotto'}</h2>
                <Link to="/cart" className="cart-badge-link">
                    Carrello {totalQuantity > 0 && <span className="cart-badge">{totalQuantity}</span>}
                </Link>
            </div>

            {loading && <p>Caricamento...</p>}
            {error && <p className="error">{error}</p>}

            {!loading && !error && variants.length === 0 && (
                <p className="muted">Prodotto non trovato.</p>
            )}

            {!loading && !error && variants.length > 0 && (
                <div className="product-detail">
                    <ImageGallery
                        images={base?.images?.length ? base.images : (base?.imageUrl ? [base.imageUrl] : [])}
                        videoUrl={base?.videoUrl}
                        alt={base?.name ?? 'Prodotto'}
                    />

                    {base?.description ? <p className="card-desc">{base.description}</p> : null}

                    <h3>Formato</h3>
                    <div className="variant-list">
                        {variants.map(v => {
                            const active = v.id === selectedProductId;
                            const label = v.variantLabel ?? 'Standard';
                            return (
                                <button
                                    key={v.id}
                                    type="button"
                                    className={active ? 'variant-button active' : 'variant-button'}
                                    onClick={() => setSelectedProductId(v.id)}
                                >
                                    <div>{label}</div>
                                    <div className="muted">€{formatEuro(v.price)}</div>
                                </button>
                            );
                        })}
                    </div>

                    <h3>Quantità</h3>
                    <div className="qty-row">
                        <button type="button" onClick={() => setQuantity(q => Math.max(1, q - 1))}>
                            −
                        </button>
                        <input
                            inputMode="numeric"
                            value={String(quantity)}
                            onChange={e => {
                                const next = Number(e.target.value);
                                if (Number.isFinite(next)) setQuantity(Math.max(1, Math.floor(next)));
                            }}
                            aria-label="Quantità"
                        />
                        <button type="button" onClick={() => setQuantity(q => q + 1)}>
                            +
                        </button>
                    </div>

                    <p>
                        <strong>Totale:</strong> €{formatEuro(total)}
                    </p>

                    <button
                        className={`add-to-cart ${justAdded ? 'added' : ''}`}
                        type="button"
                        onClick={onAdd}
                        disabled={!selectedVariant}
                    >
                        {justAdded ? 'Aggiunto!' : 'Aggiungi al carrello'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default ProductPage;
